import {
  Point,
  Series,
  SeriesLegendItemClickEventObject,
  YAxisOptions,
  AxisLabelsFormatterContextObject,
  OptionsChartZoomingTypeValue,
  Options,
  XAxisPlotBandsOptions,
} from 'highcharts';

interface ChartConfigParams {
  redrawEvent: () => void;
  xAxisFormatter: (context: AxisLabelsFormatterContextObject) => string;
  yAxis: YAxisOptions | YAxisOptions[];
  plotBands: XAxisPlotBandsOptions[];
  displayDateTime: (data: number | string, dateTimeFormat: string) => string;
}

export function getBaseChartConfig(params: ChartConfigParams): Options {
  return {
    boost: {
      useGPUTranslations: true,
      seriesThreshold: 1,
      usePreallocated: true,
      pixelRatio: 1,
    },
    scrollbar: {
      barBorderRadius: 6,
      barBorderWidth: 0,
      buttonArrowColor: 'transparent',
      buttonBackgroundColor: 'transparent',
      buttonBorderColor: 'transparent',
      barBackgroundColor: '#cacaca',
      height: 6,
      minWidth: 6,
      trackBackgroundColor: 'transparent',
      barBorderColor: 'transparent',
      trackBorderColor: 'transparent',
      rifleColor: 'transparent',
    },
    chart: {
      height: 500,
      animation: false,
      type: 'line',
      style: {
        fontFamily: 'var(--fonts)',
      },
      zooming: {
        type: 'x' as OptionsChartZoomingTypeValue,
      },
      events: {
        redraw: params.redrawEvent,
      },
    },
    legend: {
      enabled: true,
      itemStyle: {
        fontFamily: 'var(--font)',
      },
    },
    mapNavigation: {
      enableMouseWheelZoom: true,
    },
    title: {
      text: '',
      align: 'left' as const,
      margin: 25,
      style: {
        color: '#333333',
        fontSize: '14px',
      },
    },
    yAxis: params.yAxis,
    xAxis: {
      type: 'datetime' as const,
      labels: {
        rotation: -45,
        useHTML: true,
        style: {
          color: '#999999',
          fontSize: '12px',
          paddingLeft: '5px',
          paddingRight: '5px',
          textAlign: 'center',
        },
        allowOverlap: false,
        formatter: params.xAxisFormatter,
      },
      lineColor: '#ccd6eb',
      tickColor: '#ccd6eb',
      plotBands: params.plotBands,
      startOnTick: false,
      endOnTick: false,
      angle: 0,
      maxPadding: 0,
      minPadding: 0,
    },
    navigator: {
      adaptToUpdatedData: false,
      enabled: true,
      maskFill: 'rgba(102,133,194,0.2)',
      outlineColor: '#d9d9d9',
      handles: {
        borderColor: '#d9d9d9',
      },
      series: {
        dataGrouping: {
          enabled: false,
        },
        fillOpacity: 0.2,
      },
    },
    plotOptions: {
      line: {
        dataGrouping: {
          enabled: false,
        },
      },
      series: {
        pointInterval: 90000,
        boostThreshold: 1,
        states: {
          hover: {
            enabled: true,
          },
        },
        events: {
          legendItemClick: function (
            event: SeriesLegendItemClickEventObject,
          ): void {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const that: Series = this;
            const visibleLegends = that.chart.legend.allItems.filter(
              legend => legend.visible,
            );
            if (
              visibleLegends.length === 1 &&
              visibleLegends[0].name === event.target.name
            ) {
              event.preventDefault();
            }
          },
        },
        lineWidth: 1,
        showInLegend: true,
        point: {
          events: {
            mouseOver: function () {
              // eslint-disable-next-line @typescript-eslint/no-this-alias
              const that = this;
              const halo = (that.series as any).halo;
              if (halo) {
                halo
                  .attr({
                    class: 'highcharts-tracker',
                  })
                  .toFront();
              }
            },
            click: function (): void {
              // eslint-disable-next-line @typescript-eslint/no-this-alias
              const point: Point = this;
              if (
                point.series.options.className?.indexOf('popup-on-click') !== -1
              ) {
                const chart = point.series.chart;
                const date = params.displayDateTime(
                  point.x,
                  'Do MMMM YYYY h:mm:ss a z',
                );
                const text = `<b>${date}</b><br/>${point.y.toFixed(2)} kW | ${
                  point.series.name
                }`;

                const anchorX = this.plotX + point.series.xAxis['pos'];
                const anchorY = this.plotY + point.series.yAxis['pos'];
                const align =
                  anchorX < chart.chartWidth - 200 ? 'left' : 'right';
                const x = align === 'left' ? anchorX + 10 : anchorX - 10;
                const y = anchorY - 30;
                if (!chart['sticky']) {
                  chart['sticky'] = chart.renderer
                    .label(text, x, y, 'callout', anchorX, anchorY)
                    .attr({
                      align,
                      fill: 'rgba(0, 0, 0, 0.75)',
                      padding: 10,
                      zIndex: 7,
                      style: { fontSize: '14px' },
                    })
                    .css({
                      color: 'white',
                      borderRadius: 10,
                    })
                    .on('click', function () {
                      chart['sticky'] = chart['sticky'].destroy();
                    })
                    .add();
                } else {
                  chart['sticky']
                    .attr({ align, text })
                    .animate({ anchorX, anchorY, x, y }, { duration: 250 });
                }
              }
            },
          },
        },
        marker: {
          radius: 1,
        },
      },
    },
  };
}
